import { describe, expect, it, vi, beforeEach, afterEach } from 'vitest';
import * as fs from 'fs';
import * as path from 'path';
import { addRoutesToConfig } from '../../src/react-router/codemods/routes-config';

vi.mock('@clack/prompts', () => {
  const mock = {
    log: {
      warn: vi.fn(),
      info: vi.fn(),
      success: vi.fn(),
    },
  };
  return {
    default: mock,
    ...mock,
  };
});

describe('addRoutesToConfig codemod', () => {
  let tmpDir: string;
  let appDir: string;
  let routesConfigPath: string;

  beforeEach(() => {
    vi.clearAllMocks();

    // Create unique tmp directory for each test
    tmpDir = path.join(
      __dirname,
      'tmp',
      `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    );
    appDir = path.join(tmpDir, 'app');
    routesConfigPath = path.join(appDir, 'routes.ts');

    fs.mkdirSync(appDir, { recursive: true });
  });

  afterEach(() => {
    // Clean up
    if (fs.existsSync(tmpDir)) {
      fs.rmSync(tmpDir, { recursive: true, force: true });
    }
  });

  it('should add routes to existing configuration', async () => {
    // Create a routes.ts file
    const routesContent = `import type { RouteConfig } from "@react-router/dev/routes";
import { index, route } from "@react-router/dev/routes";

export default [
  index("routes/home.tsx"),
  route("/about", "routes/about.tsx"),
] satisfies RouteConfig;`;

    fs.writeFileSync(routesConfigPath, routesContent);

    await addRoutesToConfig(routesConfigPath, true);

    // Check that both routes were added
    const updatedContent = fs.readFileSync(routesConfigPath, 'utf-8');
    expect(updatedContent).toContain(
      'route("/sentry-example-page", "routes/sentry-example-page.tsx")',
    );
    expect(updatedContent).toContain(
      'route("/api/sentry-example-api", "routes/api.sentry-example-api.ts")',
    );
  });

  it('should handle JavaScript projects correctly', async () => {
    // Create a routes.ts file
    const routesContent = `import type { RouteConfig } from "@react-router/dev/routes";
import { index, route } from "@react-router/dev/routes";

export default [
  index("routes/home.jsx"),
] satisfies RouteConfig;`;

    fs.writeFileSync(routesConfigPath, routesContent);

    await addRoutesToConfig(routesConfigPath, false); // JavaScript project

    // Check that both routes were added with .jsx/.js extensions
    const updatedContent = fs.readFileSync(routesConfigPath, 'utf-8');
    expect(updatedContent).toContain(
      'route("/sentry-example-page", "routes/sentry-example-page.jsx")',
    );
    expect(updatedContent).toContain(
      'route("/api/sentry-example-api", "routes/api.sentry-example-api.js")',
    );
  });

  it('should not duplicate routes if they already exist', async () => {
    // Create a routes.ts file with both routes already present
    const routesContent = `import type { RouteConfig } from "@react-router/dev/routes";
import { index, route } from "@react-router/dev/routes";

export default [
  index("routes/home.tsx"),
  route("/sentry-example-page", "routes/sentry-example-page.tsx"),
  route("/api/sentry-example-api", "routes/api.sentry-example-api.ts"),
] satisfies RouteConfig;`;

    fs.writeFileSync(routesConfigPath, routesContent);

    await addRoutesToConfig(routesConfigPath, true);

    // Check that the routes were not duplicated
    const updatedContent = fs.readFileSync(routesConfigPath, 'utf-8');
    const pageRouteMatches = updatedContent.match(
      /route\("\/sentry-example-page"/g,
    );
    const apiRouteMatches = updatedContent.match(
      /route\("\/api\/sentry-example-api"/g,
    );
    expect(pageRouteMatches).toHaveLength(1);
    expect(apiRouteMatches).toHaveLength(1);
  });

  it('should add route import when it does not exist', async () => {
    // Create a routes.ts file without route import
    const routesContent = `import type { RouteConfig } from "@react-router/dev/routes";
import { index } from "@react-router/dev/routes";

export default [
  index("routes/home.tsx"),
] satisfies RouteConfig;`;

    fs.writeFileSync(routesConfigPath, routesContent);

    await addRoutesToConfig(routesConfigPath, true);

    // Check that the route import was added
    const updatedContent = fs.readFileSync(routesConfigPath, 'utf-8');
    expect(updatedContent).toContain('route');
    expect(updatedContent).toContain(
      'route("/sentry-example-page", "routes/sentry-example-page.tsx")',
    );
  });

  it('should create default export when it does not exist', async () => {
    // Create a routes.ts file without default export
    const routesContent = `import type { RouteConfig } from "@react-router/dev/routes";
import { index, route } from "@react-router/dev/routes";`;

    fs.writeFileSync(routesConfigPath, routesContent);

    await addRoutesToConfig(routesConfigPath, true);

    // Check that the default export was created
    const updatedContent = fs.readFileSync(routesConfigPath, 'utf-8');
    expect(updatedContent).toContain('export default [');
    expect(updatedContent).toContain(
      'route("/sentry-example-page", "routes/sentry-example-page.tsx")',
    );
    expect(updatedContent).toContain(
      'route("/api/sentry-example-api", "routes/api.sentry-example-api.ts")',
    );
  });

  it('should handle empty file gracefully', async () => {
    // Create an empty routes.ts file
    fs.writeFileSync(routesConfigPath, '');

    await addRoutesToConfig(routesConfigPath, true);

    // Check that everything was added from scratch
    const updatedContent = fs.readFileSync(routesConfigPath, 'utf-8');
    expect(updatedContent).toContain(
      'import { route } from "@react-router/dev/routes";',
    );
    expect(updatedContent).toContain('export default [');
    expect(updatedContent).toContain(
      'route("/sentry-example-page", "routes/sentry-example-page.tsx")',
    );
    expect(updatedContent).toContain(
      'route("/api/sentry-example-api", "routes/api.sentry-example-api.ts")',
    );
  });

  it('should skip if file does not exist', async () => {
    // Don't create the file
    await addRoutesToConfig(routesConfigPath, true);

    // Should not create the file if it doesn't exist
    expect(fs.existsSync(routesConfigPath)).toBe(false);
  });
});
